import { HEX_DIRECTIONS, hexKey, hexToPixel, type Hex } from './hex';
import { mulberry32, shuffle, type Rng } from './rng';
import { RESOURCES, type Resource } from './resources';
import { STANDARD_HEXES, TOPOLOGY, topologyFor, type Topology } from './topology';

export type Terrain = 'hills' | 'forest' | 'pasture' | 'fields' | 'mountains' | 'desert';
export type PortType = Resource | 'any';
export type BoardLayout = 'random' | 'fixed';

export interface Tile {
  terrain: Terrain;
  token: number | null;
}

export interface Port {
  edge: number;
  type: PortType;
}

export interface Board {
  /**
   * Land hexes, parallel to `tiles`. Absent on the standard 19-hex island
   * (including every board created before shapes varied).
   */
  hexes?: Hex[];
  tiles: Tile[];
  ports: Port[];
}

export interface BoardOptions {
  playerCount?: number;
  /** Width of the middle row (see `landmassForSize`); defaults to `autoMapSize(playerCount)`. */
  mapSize?: number;
  /** Scatter the land over the island plus one ring of sea, making random islands and lakes. */
  islands?: boolean;
  /** Shuffle the number tokens with no rule keeping 6s and 8s apart. */
  scrambleNumbers?: boolean;
}

export const TERRAIN_RESOURCE: Record<Terrain, Resource | null> = {
  hills: 'brick',
  forest: 'lumber',
  pasture: 'wool',
  fields: 'grain',
  mountains: 'ore',
  desert: null,
};

/** Number tokens in the order they're laid along the spiral (skipping the desert). */
export const SPIRAL_TOKENS: readonly number[] = [5, 2, 6, 3, 8, 10, 9, 12, 11, 4, 8, 10, 9, 4, 5, 6, 3, 11];

// Terrain along the spiral for the fixed layout; desert sits in the center.
const FIXED_TERRAIN: readonly Terrain[] = [
  'forest', 'pasture', 'fields', 'hills', 'mountains', 'pasture',
  'forest', 'fields', 'hills', 'pasture', 'fields', 'mountains',
  'forest', 'hills', 'fields', 'mountains', 'pasture', 'forest',
  'desert',
];

const FIXED_PORTS: readonly PortType[] = ['any', 'grain', 'ore', 'any', 'wool', 'any', 'any', 'brick', 'lumber'];

export const MIN_MAP_SIZE = 5;
export const MAX_MAP_SIZE = 10;

/**
 * Map size by player count: the 19-hex island up to 4 players, the 30-hex
 * 3-4-5-6-5-4-3 extension for 5-6, and one more hex per row for every two
 * players beyond that (about five hexes per player).
 */
export function autoMapSize(playerCount: number): number {
  if (playerCount <= 4) return 5;
  return Math.min(MAX_MAP_SIZE, 6 + Math.ceil(Math.max(0, playerCount - 6) / 2));
}

export function landmassFor(playerCount: number): Hex[] {
  return landmassForSize(autoMapSize(playerCount));
}

/** Land shape by middle-row width: 5 is the base island; 6+ is seven rows, each one shorter than the middle per step out. */
export function landmassForSize(size: number): Hex[] {
  if (size <= 5) return STANDARD_HEXES.slice();
  const middle = Math.min(size, MAX_MAP_SIZE);
  const hexes: Hex[] = [];
  for (let r = -3; r <= 3; r++) {
    const length = middle - Math.abs(r);
    // Lengths change by one per row, so (length - 1 + r) has the same parity
    // on every row and each row sits half a hex from its neighbours.
    const start = -Math.floor((length - 1 + r) / 2);
    for (let i = 0; i < length; i++) hexes.push({ q: start + i, r });
  }
  return sortHexes(hexes);
}

/** Same order `hexesInRadius` uses, so the standard island keeps its indices. */
function sortHexes(hexes: Hex[]): Hex[] {
  return hexes.sort((a, b) => a.q - b.q || a.r - b.r);
}

function isStandardShape(hexes: readonly Hex[]): boolean {
  return hexes.length === STANDARD_HEXES.length && hexes.every((h, i) => hexKey(h) === hexKey(STANDARD_HEXES[i]!));
}

/** Splits `total` across `weights` in proportion, by largest remainder (ties go to the earlier entry). */
function apportion<T>(total: number, weights: readonly [T, number][]): T[] {
  const sum = weights.reduce((n, [, w]) => n + w, 0);
  const shares = weights.map(([k, w], i) => ({ k, i, exact: (total * w) / sum, n: Math.floor((total * w) / sum) }));
  let left = total - shares.reduce((n, s) => n + s.n, 0);
  for (const s of shares.slice().sort((a, b) => b.exact - b.n - (a.exact - a.n) || a.i - b.i)) {
    if (left-- <= 0) break;
    s.n++;
  }
  return shares.flatMap((s) => Array<T>(s.n).fill(s.k));
}

/** Terrain for `tiles` hexes, in the 5-6 player extension's proportions (the base game's at 19). */
export function terrainFor(tiles: number): Terrain[] {
  const deserts = tiles <= 19 ? 1 : tiles <= 30 ? 2 : Math.round(tiles / 15);
  return [
    ...apportion<Terrain>(tiles - deserts, [
      ['forest', 6],
      ['pasture', 6],
      ['fields', 6],
      ['hills', 5],
      ['mountains', 5],
    ]),
    ...Array<Terrain>(deserts).fill('desert'),
  ];
}

/** Number tokens for `count` producing hexes: the base set at 18, the extension's at 28. */
export function tokensFor(count: number): number[] {
  const weights: [number, number][] = [2, 3, 4, 5, 6, 8, 9, 10, 11, 12].map((n) => [n, n === 2 || n === 12 ? 2 : 3]);
  return apportion(count, weights);
}

function portCount(tiles: number): number {
  if (tiles <= 19) return 9;
  if (tiles <= 30) return 11;
  return 11 + Math.round(((tiles - 30) * 2) / 7);
}

/**
 * Roughly 4 in 9 generic, then one of each resource, then extras starting
 * with wool (the extension's added harbor). The base list's order is kept so
 * a seed shuffles to the same harbors it always did.
 */
function portTypesFor(count: number): PortType[] {
  const generic = Math.round((count * 4) / 9);
  const extras: Resource[] = ['wool', 'grain', 'ore', 'brick', 'lumber'];
  const specific = Array.from({ length: count - generic }, (_, i) =>
    i < RESOURCES.length ? RESOURCES[i]! : extras[(i - RESOURCES.length) % extras.length]!,
  );
  return [...Array<PortType>(generic).fill('any'), ...specific];
}

interface Harbor {
  /** The coastal edge the harbor serves. */
  edge: number;
  /** The sea hex it sits in, as a key, so no sea hex gets two. */
  sea: string;
}

/**
 * The harbor a coastal edge's sea hex would get. A harbor belongs to its sea
 * hex, not the edge: it faces whichever land side has the longest straight
 * run of land behind it (ties toward the middle of the board).
 */
function harborFor(t: Topology, coastEdge: number): Harbor {
  const index = new Map(t.hexes.map((h, i) => [hexKey(h), i]));
  const isLand = (h: Hex) => index.has(hexKey(h));
  const [a, b] = t.edges[coastEdge]!.vertices.map((v) => t.vertices[v]!);
  const landHex = t.hexes[t.edges[coastEdge]!.hexes[0]!]!;
  const c = hexToPixel(landHex, 1);
  // The sea hex's centre is the land hex's centre mirrored across the edge.
  const sx = a!.x + b!.x - c.x;
  const sy = a!.y + b!.y - c.y;
  const dir = HEX_DIRECTIONS.find((d) => {
    const n = hexToPixel({ q: landHex.q + d.q, r: landHex.r + d.r }, 1);
    return Math.abs(n.x - sx) < 1e-6 && Math.abs(n.y - sy) < 1e-6;
  })!;
  const sea = { q: landHex.q + dir.q, r: landHex.r + dir.r };
  const mid = t.hexes.map((h) => hexToPixel(h, 1)).reduce((m, p) => ({ x: m.x + p.x / t.hexes.length, y: m.y + p.y / t.hexes.length }), { x: 0, y: 0 });
  const centre = hexToPixel(sea, 1);
  let best: { d: Hex; run: number; toward: number } | null = null;
  for (const d of HEX_DIRECTIONS) {
    let run = 0;
    while (isLand({ q: sea.q + d.q * (run + 1), r: sea.r + d.r * (run + 1) })) run++;
    if (run === 0) continue;
    const step = hexToPixel({ q: sea.q + d.q, r: sea.r + d.r }, 1);
    const toward = (step.x - centre.x) * (mid.x - centre.x) + (step.y - centre.y) * (mid.y - centre.y);
    if (!best || run > best.run || (run === best.run && toward > best.toward + 1e-9)) best = { d, run, toward };
  }
  const land = index.get(hexKey({ q: sea.q + best!.d.q, r: sea.r + best!.d.r }))!;
  const landCentre = hexToPixel(t.hexes[land]!, 1);
  const ex = (centre.x + landCentre.x) / 2;
  const ey = (centre.y + landCentre.y) / 2;
  const edge = t.hexEdges[land]!.find((e) => {
    const [p, q] = t.edges[e]!.vertices.map((v) => t.vertices[v]!);
    return Math.abs((p!.x + q!.x) / 2 - ex) < 1e-6 && Math.abs((p!.y + q!.y) / 2 - ey) < 1e-6;
  })!;
  return { edge, sea: hexKey(sea) };
}

/**
 * Spreads `count` harbors evenly around the coast. Each sea hex holds at most
 * one, facing its longest line of land (`harborFor`), and no two harbors share
 * a corner.
 */
function portEdges(t: Topology, count: number): number[] {
  const taken = new Set<number>();
  const seas = new Set<string>();
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const want = Math.floor((i * t.coast.length) / count);
    for (let step = 0; step < t.coast.length; step++) {
      const harbor = harborFor(t, t.coast[(want + step) % t.coast.length]!);
      const ends = t.edges[harbor.edge]!.vertices;
      if (seas.has(harbor.sea) || ends.some((v) => taken.has(v))) continue;
      seas.add(harbor.sea);
      ends.forEach((v) => taken.add(v));
      out.push(harbor.edge);
      break;
    }
  }
  return out;
}

function tokensAlongSpiral(t: Topology, terrainByHex: Terrain[], tokens: readonly number[]): (number | null)[] {
  const out: (number | null)[] = terrainByHex.map(() => null);
  let next = 0;
  for (const hex of t.spiral) {
    if (terrainByHex[hex] === 'desert') continue;
    out[hex] = tokens[next++]!;
  }
  return out;
}

export function hasAdjacentRedNumbers(tiles: readonly Tile[], t: Topology = TOPOLOGY): boolean {
  const red = (x: Tile | undefined) => x?.token === 6 || x?.token === 8;
  return tiles.some((x, i) => red(x) && t.hexNeighbors[i]!.some((n) => red(tiles[n])));
}

/** The island shape plus one ring of sea around it. */
function withSeaRing(land: readonly Hex[]): Hex[] {
  const seen = new Set(land.map(hexKey));
  const out = land.slice();
  for (const h of land) {
    for (const d of HEX_DIRECTIONS) {
      const n = { q: h.q + d.q, r: h.r + d.r };
      if (!seen.has(hexKey(n))) {
        seen.add(hexKey(n));
        out.push(n);
      }
    }
  }
  return out;
}

export function generateBoard(layout: BoardLayout, rng: Rng = mulberry32(0), opts: BoardOptions = {}): Board {
  const shape = landmassForSize(opts.mapSize ?? autoMapSize(opts.playerCount ?? 4));
  // Islands keep the same number of land hexes but scatter them across the
  // island and its surrounding ring of sea.
  const hexes = opts.islands ? sortHexes(shuffle(rng, withSeaRing(shape)).slice(0, shape.length)) : shape;
  const standard = isStandardShape(hexes);
  const t = topologyFor(hexes);
  const ports = portEdges(t, portCount(hexes.length));

  let terrain: Terrain[];
  let tiles: Tile[];
  if (layout === 'fixed' && standard) {
    terrain = [];
    t.spiral.forEach((hex, i) => (terrain[hex] = FIXED_TERRAIN[i]!));
    const tokens = opts.scrambleNumbers ? shuffle(rng, SPIRAL_TOKENS) : SPIRAL_TOKENS;
    const byHex = tokensAlongSpiral(t, terrain, tokens);
    tiles = terrain.map((x, i) => ({ terrain: x, token: byHex[i]! }));
    return { tiles, ports: ports.map((edge, i) => ({ edge, type: FIXED_PORTS[i]! })) };
  }

  terrain = shuffle(rng, terrainFor(hexes.length));
  const numbers = standard ? SPIRAL_TOKENS.slice() : tokensFor(terrain.filter((x) => x !== 'desert').length);
  const place = (order: readonly number[]): Tile[] => {
    let k = 0;
    return terrain.map((x) => ({ terrain: x, token: x === 'desert' ? null : order[k++]! }));
  };
  if (opts.scrambleNumbers) {
    tiles = place(shuffle(rng, numbers));
  } else {
    // Rejection-sample token placement until no 6/8 touch. A random shuffle
    // passes often enough that this finishes in a handful of tries; the spiral
    // fallback only exists so the loop is provably bounded.
    tiles = [];
    for (let attempt = 0; attempt < 1000; attempt++) {
      tiles = place(shuffle(rng, numbers));
      if (!hasAdjacentRedNumbers(tiles, t)) break;
    }
    if (hasAdjacentRedNumbers(tiles, t)) {
      const byHex = tokensAlongSpiral(t, terrain, numbers);
      tiles = terrain.map((x, i) => ({ terrain: x, token: byHex[i]! }));
    }
  }
  const types = shuffle(rng, portTypesFor(ports.length));
  return {
    ...(standard ? {} : { hexes }),
    tiles,
    ports: ports.map((edge, i) => ({ edge, type: types[i]! })),
  };
}
